import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { ExternalLink, Award, Building2, BookOpen, Sparkles } from "lucide-react";

function Portrait({ c }) {
  const [failed, setFailed] = useState(false);
  const monogram = (c.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const size = 168;
  const style = { width: size, height: size };
  if (failed || !c.photo_url) {
    return (
      <div className="rounded-2xl grid place-items-center shrink-0 font-mono font-bold" style={{ ...style, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: size / 3 }}>
        {monogram}
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden shrink-0 border" style={{ ...style, borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
      <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" onError={() => setFailed(true)} />
    </div>
  );
}

function BioSection({ c }) {
  const [pending, setPending] = useState(!c.ai_bio);
  const [bio, setBio] = useState(c.ai_bio);

  useEffect(() => {
    setBio(c.ai_bio);
    setPending(!c.ai_bio);
  }, [c.ai_bio, c.id]);

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await http.get(`/world-changers/${c.slug || c.id}`);
        if (cancelled) return;
        if (data.ai_bio) { setBio(data.ai_bio); setPending(false); return; }
      } catch {}
      if (attempts < 40 && !cancelled) setTimeout(tick, 5000);
      else if (!cancelled) setPending(false);
    };
    const t = setTimeout(tick, 5000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pending, c.slug, c.id]);

  if (!bio) {
    if (pending) {
      return (
        <section data-testid="changer-bio-pending" className="wofy-card">
          <div className="overline flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--wofy-primary)" }} /> BIOGRAPHY</div>
          <div className="font-display text-xl mt-2">Weaving {c.name}'s story…</div>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>Our AI editor is drafting a long-form profile. Refresh in ~30 seconds or leave this tab open.</p>
        </section>
      );
    }
    return null;
  }

  return (
    <section data-testid="changer-bio">
      {bio.short_bio && (
        <div className="wofy-card mb-6">
          <div className="overline">AT A GLANCE</div>
          <p className="mt-3 text-base leading-relaxed">{bio.short_bio}</p>
        </div>
      )}
      <article>
        <div className="overline">EDITORIAL PROFILE · AI-ASSISTED</div>
        <h2 className="font-display text-2xl mt-2">About {c.name}</h2>
        {bio.lead && (
          <p className="mt-5 text-lg leading-relaxed first-letter:font-display first-letter:text-5xl first-letter:mr-2 first-letter:float-left first-letter:leading-none" style={{ color: "var(--wofy-text)" }}>
            {bio.lead}
          </p>
        )}
        <div className="space-y-8 mt-8">
          {bio.sections?.map((s, i) => (
            <div key={i}>
              <h3 className="font-display text-xl mb-2">{s.heading}</h3>
              <div className="space-y-3 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                {s.paragraphs?.map((p, j) => (<p key={j}>{p}</p>))}
              </div>
            </div>
          ))}
        </div>
        {bio.notable_quotes?.length > 0 && (
          <div className="mt-10">
            <div className="overline mb-3">IN THEIR OWN WORDS</div>
            <div className="space-y-4">
              {bio.notable_quotes.map((q, i) => (
                <blockquote key={i} className="border-l-4 pl-5 py-1 font-display italic text-lg leading-snug" style={{ borderColor: "var(--wofy-primary)" }}>
                  “{q}”
                </blockquote>
              ))}
            </div>
          </div>
        )}
        <p className="mt-10 text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
          This profile ({bio.word_count || 0} words) was synthesised with AI assistance from publicly available information about {c.name}. Please verify facts against the linked Wikipedia article and other primary sources.
        </p>
      </article>
    </section>
  );
}

export default function WorldChangerDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setC(null);
    setNotFound(false);
    setRelated([]);
    http.get(`/world-changers/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
    http.get(`/world-changers/${slug}/related`, { params: { limit: 6 } })
      .then((r) => setRelated(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRelated([]));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="overline">WORLD CHANGERS</div>
        <h1 className="font-display text-3xl mt-3">We couldn't find that profile.</h1>
        <Link to="/world-changers" className="wofy-btn-primary mt-6 inline-flex">Back to directory</Link>
      </div>
    );
  }
  if (!c) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">Loading…</div>;

  const years = [c.born?.slice(0, 4), c.died?.slice(0, 4) || "present"].filter(Boolean).join(" – ");
  const canonicalUrl = `https://worldofjoy.com/world-changers/${c.slug}`;
  const ogDesc = c.tagline || (c.ai_bio?.short_bio ? c.ai_bio.short_bio.slice(0, 240) : `${c.name} — ${c.occupation}`);

  return (
    <div data-testid="changer-detail" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
      <Helmet>
        <title>{`${c.name} — World Changers · Social Pulse`}</title>
        <meta name="description" content={ogDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Social Pulse" />
        <meta property="og:title" content={`${c.name} — World Changers · Social Pulse`} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:url" content={canonicalUrl} />
        {c.photo_url && <meta property="og:image" content={c.photo_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${c.name} — World Changers`} />
        <meta name="twitter:description" content={ogDesc} />
        {c.photo_url && <meta name="twitter:image" content={c.photo_url} />}
      </Helmet>

      <div className="text-[11px] font-mono uppercase tracking-wider mb-6 opacity-60">
        <Link to="/world-changers" className="hover:opacity-100">World Changers</Link>
        <span className="mx-2">/</span>
        <span>{c.name}</span>
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:items-center">
        <Portrait c={c} />
        <div className="flex-1 min-w-0">
          <div className="overline flex flex-wrap items-center gap-2">
            {c.nationality && <span>{c.nationality.toUpperCase()}</span>}
            {years && <span>· {years}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">{c.name}</h1>
          {c.alt_names?.length > 0 && (
            <div className="text-sm font-mono uppercase tracking-wider mt-1 opacity-60">Also known as {c.alt_names.join(", ")}</div>
          )}
          {c.occupation && <div className="mt-3 text-base" style={{ color: "var(--wofy-text-soft)" }}>{c.occupation}</div>}
          {c.tagline && <p className="mt-4 text-lg leading-relaxed max-w-3xl">{c.tagline}</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 mt-12">
        <div className="lg:col-span-8 space-y-10">
          {c.categories?.length > 0 && (
            <section data-testid="changer-categories">
              <div className="overline">AREAS OF IMPACT</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.categories.map((k) => (
                  <Link key={k} to={`/world-changers?category=${encodeURIComponent(k)}`} className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border hover:bg-[color:var(--wofy-surface)]" style={{ borderColor: "var(--wofy-border)" }}>{k}</Link>
                ))}
              </div>
            </section>
          )}

          <BioSection c={c} />

          {c.ai_bio?.timeline?.length > 0 && (
            <section data-testid="changer-timeline">
              <h2 className="font-display text-2xl mb-4">Life Timeline</h2>
              <ol className="relative border-l-2 pl-6 space-y-5" style={{ borderColor: "var(--wofy-border)" }}>
                {c.ai_bio.timeline.map((e, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[30px] top-1.5 w-3 h-3 rounded-full" style={{ background: "var(--wofy-primary)" }} />
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70">{e.year}</div>
                    <div className="font-display text-lg mt-0.5">{e.title}</div>
                    {e.description && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{e.description}</p>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {c.ai_bio?.how_to_learn_more?.length > 0 && (
            <section data-testid="changer-learn-more" className="wofy-card">
              <div className="overline">HOW TO LEARN MORE</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {c.ai_bio.how_to_learn_more.map((h, i) => (
                  <li key={i} className="flex items-start gap-2"><span style={{ color: "var(--wofy-primary)" }}>›</span><span>{h}</span></li>
                ))}
              </ul>
            </section>
          )}

          {related.length > 0 && (
            <section data-testid="changer-related">
              <div className="overline mb-3">RELATED WORLD CHANGERS</div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/world-changers/${r.slug || r.id}`}
                    data-testid={`related-${r.slug || r.id}`}
                    className="wofy-card !p-4 flex items-center gap-3 hover:border-[color:var(--wofy-primary)] transition-colors"
                  >
                    <img
                      src={r.photo_url || ""}
                      alt={r.name}
                      className="w-14 h-14 rounded-full object-cover border shrink-0"
                      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-sm leading-tight line-clamp-2">{r.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mt-0.5 line-clamp-1">
                        {(r.categories || []).slice(0, 2).join(" · ")}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-6">
          {c.wiki_url && (
            <a href={c.wiki_url} target="_blank" rel="noopener noreferrer" data-testid="changer-wiki-link" className="wofy-card block hover:border-[color:var(--wofy-primary)] transition-colors">
              <div className="overline">CANONICAL SOURCE</div>
              <div className="font-display text-lg mt-2 flex items-center gap-2">Wikipedia article <ExternalLink className="w-4 h-4" /></div>
              <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>Open the definitive reference source in a new tab.</p>
            </a>
          )}

          {c.ai_bio?.awards?.length > 0 && (
            <div className="wofy-card">
              <div className="overline flex items-center gap-1.5"><Award className="w-3 h-3" /> AWARDS & HONOURS</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {c.ai_bio.awards.slice(0, 10).map((a, i) => <li key={i} className="flex items-start gap-2"><span style={{ color: "var(--wofy-primary)" }}>›</span><span>{a}</span></li>)}
              </ul>
            </div>
          )}

          {c.ai_bio?.organizations?.length > 0 && (
            <div className="wofy-card">
              <div className="overline flex items-center gap-1.5"><Building2 className="w-3 h-3" /> ORGANIZATIONS</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {c.ai_bio.organizations.slice(0, 8).map((o, i) => <li key={i} className="flex items-start gap-2"><span style={{ color: "var(--wofy-primary)" }}>›</span><span>{o}</span></li>)}
              </ul>
            </div>
          )}

          <div className="text-[11px] leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            <BookOpen className="w-3 h-3 inline mr-1" /> Social Pulse is not affiliated with {c.name} or their estate. Information is compiled from public sources.
          </div>
        </aside>
      </div>
    </div>
  );
}
